/**
 * Hospital-specific risk multipliers for different activity categories
 */
export const HOSPITAL_MULTIPLIERS = {
  infectiousDisease: 1.2,    // +20% for infection control
  regulatoryIntensity: 1.15, // +15% for Joint Commission/CMS
  lifeSafety: 1.25,          // +25% for fire/life safety
  researchLabs: 1.3,         // +30% for BSL-2/3 labs
  surgery: 1.2,              // +20% for surgical suites
  radiology: 1.15,           // +15% for imaging/radiation
  pharmacy: 1.2,             // +20% for hazardous drugs
  default: 1.0               // No multiplier
};

/**
 * Hospital department risk categories
 */
export const HOSPITAL_DEPARTMENTS = {
  surgery: { label: 'Surgery/Operating Rooms', risk: 'high', multiplier: 'surgery' },
  labs: { label: 'Laboratories/Research', risk: 'high', multiplier: 'researchLabs' },
  radiology: { label: 'Radiology/Imaging', risk: 'high', multiplier: 'radiology' },
  emergency: { label: 'Emergency Department', risk: 'medium', multiplier: 'regulatoryIntensity' },
  pharmacy: { label: 'Pharmacy/Hazardous Drugs', risk: 'medium', multiplier: 'pharmacy' },
  facilities: { label: 'Facilities/Engineering', risk: 'medium', multiplier: 'default' },
  administration: { label: 'Administration', risk: 'low', multiplier: 'default' },
  outpatient: { label: 'Outpatient Clinics', risk: 'low', multiplier: 'default' }
};

/**
 * Calculate weighted hours for a product based on risk factors
 * Formula: baseHours × (1 + comp/100 + risk/100 + env/100) × hospitalMultiplier
 * FIXED: Added validation and bounds checking
 */
export const getWeightedHours = (product, inputs = {}) => {
  if (!product || product.enabled === false || !product.baseHours) return 0;
  
  // Validate and bound the percentages (0-100%)
  const comp = Math.max(0, Math.min(100, product.comp || 0));
  const risk = Math.max(0, Math.min(100, product.risk || 0));
  const env = Math.max(0, Math.min(100, product.env || 0));
  
  const baseWeights = 1 + (comp / 100) + (risk / 100) + (env / 100);
  const baseWeighted = Math.max(0, product.baseHours) * baseWeights;
  
  // Apply hospital multiplier if category is specified
  if (product.category && inputs.facilityType === 'hospital') {
    const multiplier = HOSPITAL_MULTIPLIERS[product.category] || 1.0;
    return baseWeighted * multiplier;
  }
  
  return baseWeighted;
};

/**
 * Calculate fire safety workload hours for hospitals
 */
export const calculateFireSafetyHours = (inputs) => {
  if (!inputs.squareFootage && !inputs.buildingCount) return 0;
  
  const sqft = inputs.squareFootage || 0;
  const buildings = inputs.buildingCount || 0;
  const occupancy = (inputs.employeeCount || 0) + (inputs.patientBeds || 0);
  
  // Base inspection time: 2 hrs per 10,000 sq ft quarterly
  const inspectionHours = (sqft / 10000) * 2 * 4;
  
  // Fire drills: 4 hrs per building per quarter (planning + execution)
  const drillHours = buildings * 4 * 4;
  
  // Hot work permits: 30 min per permit (estimate 100/year for hospitals)
  const permitHours = 100 * 0.5;
  
  // Life Safety Code compliance: 260 hrs/year base
  const lifeSafetyBase = 260;
  
  // Training: 1 hr per 50 occupants annually
  const trainingHours = occupancy > 0 ? occupancy / 50 : 0;
  
  return inspectionHours + drillHours + permitHours + lifeSafetyBase + trainingHours;
};

/**
 * Calculate environmental setbacks workload for hospitals
 */
export const calculateEnvironmentalHours = (inputs) => {
  const beds = inputs.patientBeds || 0;
  const labs = inputs.labCount || 0;
  const emissionSources = inputs.emissionSources || 0;
  
  // Medical waste generation (regulated medical waste)
  const medicalWasteHours = 312 + (beds * 0.5);
  
  // Wastewater discharge (surgical, lab, imaging)
  const waterDischargeHours = 208 * (inputs.facilityComplexity || 1);
  
  // Hazardous chemical storage (pharmacy, labs)
  const hazMatHours = 260 + (labs * 52);
  
  // Air emissions (emergency generators, sterilizers)
  const airQualityHours = 156 + (emissionSources * 26);
  
  return medicalWasteHours + waterDischargeHours + hazMatHours + airQualityHours;
};

/**
 * Calculate population-based scaling factor for validation
 * Returns benchmark FTE based on industry standards
 */
export const calculatePopulationBenchmark = (inputs) => {
  const employees = inputs.employeeCount || 0;
  const beds = inputs.patientBeds || 0;
  const sqft = inputs.squareFootage || 0;
  
  // Industry benchmark: 1 EHS FTE per 500-800 employees (hospitals)
  const employeeRatio = employees > 0 ? employees / 650 : 0;
  
  // Add bed-based component (1 FTE per 400 beds)
  const bedRatio = beds > 0 ? beds / 400 : 0;
  
  // Add facility size component (1 FTE per 400,000 sq ft)
  const sizeRatio = sqft > 0 ? sqft / 400000 : 0;
  
  // Return weighted average (prioritize employee count if available)
  if (employees > 0) {
    return bedRatio > 0 ? (employeeRatio * 0.6 + bedRatio * 0.4) : employeeRatio;
  } else if (beds > 0) {
    return bedRatio;
  } else if (sqft > 0) {
    return sizeRatio;
  }
  
  return 0;
};

/**
 * Main FTE calculation model
 * Returns complete breakdown of FTE requirements and costs
 */
export const calculateModel = (products, inputs, teamData = null) => {
  // 1. Calculate Productive Hours (H_FTE)
  const availableHours = inputs.totalHours - inputs.leaveHours;
  const adminHours = availableHours * (inputs.adminPercent / 100);
  const productiveHoursFTE = availableHours - adminHours;

  // 2. Calculate Activity-Based Hours (H_Activity) - only enabled products
  const totalActivityHours = products
    .filter(p => p.enabled !== false)
    .reduce((sum, product) => sum + getWeightedHours(product, inputs), 0);

  // 3. Calculate Fire Safety Hours (H_FireSafety) - NEW
  const fireSafetyHours = inputs.facilityType === 'hospital' ? calculateFireSafetyHours(inputs) : 0;
  
  // 4. Calculate Environmental Setbacks Hours (H_Environmental) - NEW
  const environmentalHours = inputs.facilityType === 'hospital' ? calculateEnvironmentalHours(inputs) : 0;

  // 5. Calculate Total Workload (H_Total) - properly scaled by employee count
  // For hospitals: Base activities scale with employee count, not as a simple multiplier
  const employeeScalingFactor = inputs.employeeCount > 0 ? Math.max(1.0, inputs.employeeCount / 1000) : 1.0;
  const scaledActivityHours = totalActivityHours * employeeScalingFactor;
  const totalWorkloadHours = inputs.coreHours + scaledActivityHours + inputs.logisticsHours + fireSafetyHours + environmentalHours;

  // 6. Calculate Final FTEs
  const totalFTEs = productiveHoursFTE > 0 ? totalWorkloadHours / productiveHoursFTE : 0;
  
  // 7. Calculate Population-Based Benchmark (NEW)
  const benchmarkFTE = calculatePopulationBenchmark(inputs);
  const benchmarkVariance = benchmarkFTE > 0 ? Math.abs(totalFTEs - benchmarkFTE) / benchmarkFTE : 0;
  const benchmarkStatus = benchmarkVariance < 0.15 ? 'good' : benchmarkVariance < 0.30 ? 'warning' : 'alert';

  // 8. Calculate Costs
  const baseSalaryCost = totalFTEs * inputs.avgSalary;
  const benefitsCost = baseSalaryCost * (inputs.benefitsPercent / 100);
  const totalCost = baseSalaryCost + benefitsCost;

  // 9. Calculate Efficiency Metrics
  const enabledProductCount = products.filter(p => p.enabled !== false).length;
  const hoursPerProduct = enabledProductCount > 0 ? totalActivityHours / enabledProductCount : 0;
  const utilization = inputs.totalHours > 0 ? (productiveHoursFTE / inputs.totalHours) * 100 : 0;
  const costPerProduct = enabledProductCount > 0 ? totalCost / enabledProductCount : 0;
  const costPerFTE = totalFTEs > 0 ? totalCost / totalFTEs : 0;
  
  // Calculate average risk level
  const avgRisk = enabledProductCount > 0
    ? products
        .filter(p => p.enabled !== false)
        .reduce((sum, p) => sum + p.risk, 0) / enabledProductCount
    : 0;

  return {
    ...inputs,
    availableHours,
    adminHours,
    productiveHoursFTE,
    totalActivityHours,
    scaledActivityHours,
    fireSafetyHours,        // NEW
    environmentalHours,     // NEW
    totalWorkloadHours,
    totalFTEs,
    benchmarkFTE,           // NEW
    benchmarkVariance,      // NEW
    benchmarkStatus,        // NEW
    baseSalaryCost,
    benefitsCost,
    totalCost,
    hoursPerProduct,
    utilization,
    costPerProduct,
    costPerFTE,
    avgRisk,
    employeeScalingFactor,
    // Current FTE and deficit
    currentFTE: teamData?.currentFTE || 0,
    fteDeficit: teamData?.currentFTE ? totalFTEs - teamData.currentFTE : 0
  };
};

/**
 * Format number as currency
 */
export const formatCurrency = (value) => {
  return `$${Math.round(value).toLocaleString()}`;
};

/**
 * Format number with commas
 */
export const formatNumber = (value, decimals = 0) => {
  return decimals > 0 ? value.toFixed(decimals) : Math.round(value).toLocaleString();
};

/**
 * Default global assumptions
 */
export const defaultInputs = {
  totalHours: 2080,
  leaveHours: 200,
  adminPercent: 15,
  coreHours: 520,
  logisticsHours: 260,
  avgSalary: 85000,
  benefitsPercent: 30,
  employeeCount: 1000,
  // Hospital-specific inputs
  facilityType: 'general',      // 'general' or 'hospital'
  patientBeds: 0,
  squareFootage: 0,
  buildingCount: 1,
  labCount: 0,
  emissionSources: 0,
  facilityComplexity: 1         // 1 = basic, 2 = research/teaching hospital
};
